import baseStyleText from './as-button-base.css?raw';
import { asApp } from '../../../util/singletons.ts';
import { sendEv } from '../../../data/events.ts';

/**
 * Shared base for all button components (as-button, as-text-button, as-icon-button).
 *
 * Provides:
 *   - _baseSheet: shared CSSStyleSheet (font-face, host defaults, size="s" scale var)
 *   - toggledOn state + forceToggleState()
 *   - click() delegation to the inner shadow <button>
 *   - _dispatchButtonClick() for consistent as-button-click event dispatch
 *   - _syncToggleState() hook for subclasses to update visual toggle state
 */
export abstract class AsButtonBase extends HTMLElement {
  static readonly _baseSheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(baseStyleText);
    return s;
  })();
  toggledOn = false;

  /** Programmatically click the inner shadow <button>. */
  click(): void {
    this.shadowRoot?.querySelector('button')?.click();
  }

  /** Force the toggle visual state. Only works when the `toggle` attribute is present. */
  forceToggleState(on: boolean): void {
    if (!this.hasAttribute('toggle')) return;
    this.toggledOn = on;
    this._syncToggleState();
  }

  /** Sync visual toggle state to `this.toggledOn`. Override in subclasses that support toggle. */
  protected _syncToggleState(): void {}

  /** Dispatch the standard as-button-click event. */
  protected _dispatchButtonClick(detail: {
    buttonName: string | null;
    toggledOn?: boolean;
    originalEvent?: MouseEvent;
  }): void {
    sendEv(this, 'as-button-click', { detail });
  }

  /**
   * Sync the `link` overlay anchor. When the `link` attribute is set, a real
   * `<a>` is kept as a light-DOM child of this element (slotted into the
   * subclass's `<slot name="link-overlay">`) and positioned to cover the
   * whole rendered button.
   *
   * This has to be a genuine, connected, light-DOM anchor rather than one
   * built into the shadow-DOM button: some embedding hosts (e.g. GrabCAD)
   * only intercept real anchor clicks the user actually makes — scripted
   * opens (`window.open`, a detached anchor's `.click()`) do nothing there,
   * and a shadow-DOM anchor gets retargeted to its shadow host before such a
   * host's click handling ever sees it as a link. Being a light-DOM child of
   * *this* element is enough even if this element itself is slotted deep
   * inside other components' shadow roots elsewhere (e.g. as-header) — the
   * anchor's own ancestor chain never crosses a shadow boundary, so it is
   * never retargeted.
   *
   * The overlay sits visually on top of the shadow-rendered button, which
   * would otherwise steal hover from it, so pointer enter/leave are forwarded
   * to the button underneath to keep hover-triggered tooltips working.
   */
  protected _syncLinkOverlay(): void {
    const href = this.getAttribute('link');
    let anchor = this.querySelector<HTMLAnchorElement>(':scope > a.as-btn-link-overlay');

    if (!href) {
      anchor?.remove();
      return;
    }

    if (!anchor) {
      anchor = document.createElement('a');
      anchor.className = 'as-btn-link-overlay';
      anchor.slot = 'link-overlay';
      anchor.addEventListener('click', this._handleLinkOverlayClick);
      anchor.addEventListener('pointerenter', this._forwardHoverToButton);
      anchor.addEventListener('pointerleave', this._forwardHoverToButton);
      this.appendChild(anchor);
    }

    const inert = this.hasAttribute('disabled') || this.hasAttribute('pending');
    if (inert) {
      anchor.setAttribute('aria-disabled', 'true');
      anchor.removeAttribute('href');
    } else {
      anchor.removeAttribute('aria-disabled');
      anchor.setAttribute('href', href);
    }

    if (this.hasAttribute('link-new-tab')) {
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
    } else {
      anchor.removeAttribute('target');
      anchor.removeAttribute('rel');
    }
  }

  private _handleLinkOverlayClick = (e: MouseEvent): void => {
    this._dispatchButtonClick({
      buttonName: this.getAttribute('name') ?? this.getAttribute('button-id'),
      toggledOn: this.toggledOn,
      originalEvent: e,
    });
    this._navigateLink(e);
  };

  /**
   * Explicitly drive the `link` navigation instead of relying on the overlay
   * anchor's own default click action. Callable both from the overlay
   * anchor's own click handler and from a subclass's shadow-DOM button click
   * handler: the overlay anchor is positioned via `::slotted()` + absolute
   * positioning across the shadow boundary, which has been observed to
   * collapse to a zero-size box in some environments — when that happens,
   * real clicks land on the shadow button underneath instead of the anchor,
   * so the anchor's click listener (and its native default action) never
   * fires at all. Subclasses should call this from their own button click
   * handler as a fallback so navigation still happens in that case.
   *
   * GrabCAD's embedded webview only honors a genuine, unmodified anchor
   * click — scripted opens (window.open, location assignment) are silently
   * blocked there — so this is a no-op in that host; the overlay anchor's own
   * default action is what has to carry navigation there instead.
   */
  protected _navigateLink(e?: Event): void {
    const href = this.getAttribute('link');
    if (!href) return;

    const liveEnv = asApp()?.getAttribute('env') ?? null;
    if (liveEnv === 'grabcad') return;

    e?.preventDefault();
    if (this.hasAttribute('link-new-tab')) {
      window.open(href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = href;
    }
  }

  private _forwardHoverToButton = (e: PointerEvent): void => {
    this.shadowRoot?.querySelector('as-tooltip-wrapper button')
      ?.dispatchEvent(new PointerEvent(e.type, { bubbles: true }));
  };
}
